import { z } from 'zod';

/**
 * The numeric levers a tier can move — the catalog `EntitlementService.limit()`
 * accepts, and the columns P26b's per-tier limit grid is drawn from.
 *
 * ## Why it is a closed list, like the capabilities beside it
 *
 * `billing.tierLimits` maps a tier to the numbers it overrides, and it is edited
 * as a grid whose columns come from here — so no operator ever types a limit key
 * (U0). A free string would make a typo indistinguishable from "left at the
 * default", which is the worst possible failure for a quota.
 *
 * ## Every member MUST name a registry setting whose type is a number
 *
 * The constraint the type cannot express, and the reason the colocated spec
 * asserts it against the registry. `limit()` resolves the fallback by calling
 * `getSetting(limitKey)` with this very string, so a member naming a key that
 * does not exist — or one holding a boolean — is a runtime failure on a member's
 * request rather than a compile error. The base value is the whole fallback
 * story: a tier that overrides nothing must still get the deployment's own
 * answer.
 *
 * ## Why there is no `isBillingLimit` beside `isBillingCapability`
 *
 * The capability guard is load-bearing because a tier's capabilities are a
 * **list that gets scanned**, so a retired key in it must be skipped rather than
 * matched. A tier's limits are a **record indexed by a key the caller already
 * holds**, so a retired entry is simply never looked at and the base value
 * answers — the tolerance is structural, not a branch. A guard is added here
 * when something needs to narrow, not in advance.
 *
 * ## Why exactly one, today
 *
 * `nominations.dailyCount` is the only per-day member quota the product has.
 * Likes carry no cap at all and there is no messages-per-day setting, so there
 * is nothing else to route. A lever joins this list **with the feature that
 * charges for it** — never speculatively, because an unreferenced key is a
 * column in the admin grid that changes nothing.
 */
export const BILLING_LIMITS = ['nominations.dailyCount'] as const;

export const BillingLimitSchema = z.enum(BILLING_LIMITS);

export type BillingLimit = (typeof BILLING_LIMITS)[number];
